import { useState, useEffect } from 'react';
import { Search, Filter, Play, Clock, Target, ArrowLeft, BookOpen, Video, Pause, RotateCcw, CheckCircle, XCircle, Wind, Heart, Activity, Dumbbell } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { PatientLayout } from './patient/PatientLayout';
import { exercisesApi } from '../lib/api/exercises';
import { useAuth } from '../lib/hooks/useAuth';
import { useLogExerciseSession } from '../lib/hooks/useExercises';
import { LoadingState } from './common/LoadingState';
import { AlertCard } from './common/AlertCard';
import { EmptyState } from './common/EmptyState';

interface ExerciseLibraryProps {
  onBack?: () => void;
  onStartExercise?: () => void;
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
}

export function ExerciseLibrary({ onBack, onStartExercise, onNavigate, onLogout }: ExerciseLibraryProps) {
  const { user, loading: authLoading } = useAuth();
  
  // ✅ FIX: Use direct API calls like VitalsManagement (no React Query hooks with enabled conditions)
  const [exercisesData, setExercisesData] = useState<any>(null);
  const [customPlansData, setCustomPlansData] = useState<any>(null);
  const [sessionsData, setSessionsData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [customPlansLoading, setCustomPlansLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [customPlansError, setCustomPlansError] = useState<any>(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Timer States
  const [activeExercise, setActiveExercise] = useState<string | null>(null);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [exerciseCompleted, setExerciseCompleted] = useState<{ [key: string]: boolean }>({});
  
  // ✅ FIX: Add exercise session logging mutation
  const logSession = useLogExerciseSession();

  // Fetch exercises data
  const fetchExercises = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await exercisesApi.getExercises();
      setExercisesData(data);
    } catch (err: any) {
      setError(err);
      setExercisesData(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch BOTH standard AND custom plans using /me endpoints for current patient
  const fetchCustomPlans = async () => {
    try {
      setCustomPlansLoading(true);
      setCustomPlansError(null);
      
      // Use /me endpoints for current patient (no patientId needed)
      // Fetch both types of exercise plans in parallel
      const [standardPlansRes, customPlansRes] = await Promise.all([
        exercisesApi.getMyExercisePlans(true).catch(() => ({ plans: [] })),
        exercisesApi.getMyCustomExercisePlans(true).catch(() => ({ exercise_plans: [] }))
      ]);
      
      // Combine both types into unified structure
      const combinedData = {
        exercise_plans: [
          // Standard plans from library
          ...(standardPlansRes?.plans || []).map((plan: any) => ({
            ...plan,
            source: 'standard',
            plan_title: plan.title || plan.exercise?.name || 'Exercise Plan',
            exercises: plan.exercise ? [{
              id: plan.exercise.id,
              type: plan.exercise.category || 'Exercise',
              duration: plan.exercise.duration_minutes || 10,
              intensity: plan.exercise.difficulty || 'Moderate',
              frequency: plan.frequency_per_week || 3,
              progressiveLoad: 'Standard',
              name: plan.exercise.name,
              instructions: plan.exercise.instructions || plan.therapist_notes || ''
            }] : []
          })),
          // Custom plans
          ...(customPlansRes?.exercise_plans || []).map((plan: any) => ({
            ...plan,
            source: 'custom'
          }))
        ]
      };
      
      setCustomPlansData(combinedData);
      
      // After getting plans, fetch sessions if we have patient_id
      const fetchedPatientId = standardPlansRes?.patient_id || customPlansRes?.patient_id;
      if (fetchedPatientId && fetchedPatientId > 0) {
        // Fetch sessions now that we have patient_id
        try {
          const sessionsRes = await exercisesApi.getPatientExerciseSessions(fetchedPatientId, { days: 30 });
          setSessionsData(sessionsRes);
        } catch (err: any) {
          setSessionsData(null);
        }
      }
    } catch (err: any) {
      setCustomPlansError(err);
      setCustomPlansData(null);
    } finally {
      setCustomPlansLoading(false);
    }
  };

  // Fetch sessions data
  const fetchSessions = async () => {
    // Sessions endpoint needs patientId, so skip if not available
    if (!user?.patient_id) {
      return;
    }
    
    try {
      const data = await exercisesApi.getPatientExerciseSessions(user.patient_id, { days: 30 });
      setSessionsData(data);
    } catch (err: any) {
      setSessionsData(null);
    }
  };

  // Fetch on mount - uses /me endpoints that don't need patientId
  useEffect(() => {
    fetchExercises();
    fetchCustomPlans();
  }, []);

  const refetch = () => {
    fetchExercises();
    fetchCustomPlans();
  };

  // Timer effect - MUST be called before any returns
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerRunning && activeExercise !== null) {
      interval = setInterval(() => {
        setTimerSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, activeExercise]);

  // Show loading while authentication is loading
  if (authLoading) {
    return (
      <PatientLayout currentView="library" onNavigate={onNavigate} onLogout={onLogout}>
        <LoadingState message="Loading..." />
      </PatientLayout>
    );
  }

  // ✅ CRITICAL FIX: Check authentication BEFORE rendering anything
  // If user is null after auth loading completes, redirect to login
  if (!user) {
    return (
      <PatientLayout currentView="library" onNavigate={onNavigate} onLogout={onLogout}>
        <div className="p-4">
          <AlertCard
            type="error"
            title="Not Authenticated"
            description="Your session has expired. Please log in again."
            action={
              <Button onClick={() => window.location.href = '/login'} variant="outline">
                Go to Login
              </Button>
            }
          />
        </div>
      </PatientLayout>
    );
  }

  // Loading state - only show if main exercises are loading
  if (isLoading) {
    return (
      <PatientLayout currentView="library" onNavigate={onNavigate} onLogout={onLogout}>
        <LoadingState message="Loading your exercises..." />
      </PatientLayout>
    );
  }

  // Error state - only show error if main exercises API failed
  // Custom plans error should not block the entire page
  if (error) {
    return (
      <PatientLayout currentView="library" onNavigate={onNavigate} onLogout={onLogout}>
        <div className="p-4">
          <AlertCard
            type="error"
            title="Failed to load exercises"
            description={error?.message || 'There was an error loading the exercise library. Please try again.'}
            action={
              <Button onClick={() => refetch()} variant="outline">
                Try Again
              </Button>
            }
          />
        </div>
      </PatientLayout>
    );
  }

  // Get custom exercise plans from therapist
  const customPlans = customPlansData?.exercise_plans || [];
  const customExercises = customPlans.flatMap(plan => 
    (plan.exercises || []).map((exercise: any) => ({
      id: `custom-${plan.id}-${exercise.id}`,
      planId: plan.id,
      name: `${exercise.type} Exercise`,
      category: exercise.type, // Breathing, Cardio, Strength, Stretching
      duration: `${exercise.duration} min`,
      durationMinutes: exercise.duration,
      difficulty: exercise.intensity, // Low, Moderate, High
      frequency: `${exercise.frequency}x/week`,
      progressiveLoad: exercise.progressiveLoad,
      description: `Therapist prescribed ${exercise.type.toLowerCase()} exercise`,
      videoUrl: '#',
      steps: 1,
      benefits: [],
      isPrescribed: true, // Flag to show it's from therapist
      status: plan.status,
    }))
  );

  // Map API exercises to component format (general library)
  // ✅ FIX: Add unique prefix to avoid duplicate keys with custom exercises
  const libraryExercises = (exercisesData?.exercises || []).map((exercise, index) => ({
    id: `library-${exercise.id || index}`,  // Unique key with prefix
    originalId: exercise.id,
    name: exercise.name || '',
    category: exercise.category || 'General',
    duration: `${exercise.duration_minutes || 10} min`,
    durationMinutes: exercise.duration_minutes || 10,
    difficulty: exercise.difficulty_level || 'Beginner',
    calories: `${exercise.calories_burned || 0} kcal`,
    description: exercise.description || '',
    videoUrl: exercise.video_url || '#',
    steps: exercise.steps_count || 4,
    benefits: exercise.benefits || [],
    isPrescribed: false,
  }));

  // Combine custom and library exercises
  const allExercises = [...customExercises, ...libraryExercises];

  // Get completed sessions
  const completedSessions = sessionsData?.sessions || [];
  const completedCount = completedSessions.filter(s => s.is_completed).length;

  // Empty state
  if (allExercises.length === 0) {
    return (
      <PatientLayout currentView="library" onNavigate={onNavigate} onLogout={onLogout}>
        <div className="p-4">
          <EmptyState
            icon={BookOpen}
            title="No exercises available"
            description="No exercises have been assigned yet. Your therapist will create a personalized exercise plan for you."
          />
        </div>
      </PatientLayout>
    );
  }

  const categories = [
    { id: 'all', name: 'All Exercises', count: allExercises.length },
    { id: 'breathing', name: 'Breathing', count: allExercises.filter(e => e.category.toLowerCase() === 'breathing').length },
    { id: 'stretching', name: 'Stretching', count: allExercises.filter(e => e.category.toLowerCase() === 'stretching').length },
    { id: 'cardio', name: 'Cardio', count: allExercises.filter(e => e.category.toLowerCase() === 'cardio').length },
    { id: 'strength', name: 'Strength', count: allExercises.filter(e => e.category.toLowerCase() === 'strength').length },
  ];

  const filteredExercises = allExercises.filter((exercise) => {
    const matchesSearch = exercise.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         exercise.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || 
                           exercise.category.toLowerCase() === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]';
      case 'Intermediate':
        return 'bg-accent/10 text-accent border-accent';
      case 'Advanced':
        return 'bg-destructive/10 text-destructive border-destructive';
      default:
        return 'bg-secondary';
    }
  };

  const handleStartExercise = (exerciseId: string) => {
    const exercise = allExercises.find(e => e.id === exerciseId);
    setActiveExercise(exerciseId);
    setTimerSeconds(0);
    setIsTimerRunning(true);
    toast.success(`Started ${exercise?.name}`, {
      description: 'Timer is running...',
    });
  };

  const handlePauseExercise = () => {
    setIsTimerRunning(!isTimerRunning);
    toast.info(isTimerRunning ? 'Timer paused' : 'Timer resumed');
  };

  const handleResetExercise = () => {
    setTimerSeconds(0);
    setIsTimerRunning(false);
    toast.info('Timer reset');
  };

  const handleCompleteExercise = (exerciseId: string) => {
    const exercise = allExercises.find(e => e.id === exerciseId);
    setExerciseCompleted(prev => ({ ...prev, [exerciseId]: true }));
    setActiveExercise(null);
    setIsTimerRunning(false);
    const completedDuration = timerSeconds;
    setTimerSeconds(0);
    
    if (!exercise) {
      toast.error('Exercise not found');
      return;
    }
    
    if (!user.patient_id || user.patient_id === 0) {
      toast.error('Unable to log exercise session', {
        description: 'Patient ID not found. Please try logging out and logging in again.',
      });
      return;
    }
    
    if (exercise.isPrescribed && exercise.planId) {
      logSession.mutate({
        exercise_plan_id: exercise.planId,
        patient_id: user.patient_id,
        duration_seconds: completedDuration,
        difficulty_rating: exercise.difficulty === 'Low' ? 2 : 
                          exercise.difficulty === 'Moderate' ? 3 : 4,
        is_completed: true,
        patient_notes: `Completed ${exercise.name} from therapist plan. Duration: ${Math.round(completedDuration / 60)} min`,
      }, {
        onSuccess: (response) => {
          toast.success(`${exercise.name} completed! 🎉`, {
            description: 'Your progress has been saved.',
          });
        },
        onError: (error: any) => {
          toast.error('Failed to save exercise session', {
            description: error?.response?.data?.detail || error.message || 'Please try again.',
          });
        }
      });
    } else {
      toast.info('Exercise completed!', {
        description: 'This exercise is not from your prescribed plan. Only prescribed exercises are tracked.',
      });
    }
  };

  const handleCancelExercise = () => {
    setActiveExercise(null);
    setIsTimerRunning(false);
    setTimerSeconds(0);
    toast.info('Exercise session cancelled');
  };

  const renderExerciseCard = (exercise: typeof allExercises[0], isListView = false) => {
    const isActive = activeExercise === exercise.id;
    const isCompleted = exerciseCompleted[exercise.id];
    const prescribedSeconds = exercise.durationMinutes * 60;
    const timerProgress = isActive ? (timerSeconds / prescribedSeconds) * 100 : 0;

    if (isListView) {
      return (
        <Card key={exercise.id} className={`shadow-lg transition-all ${isActive ? 'border-2 border-primary' : ''}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Video className="w-8 h-8 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-primary">{exercise.name}</h3>
                  {exercise.isPrescribed && (
                    <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4] text-xs">
                      Prescribed
                    </Badge>
                  )}
                </div>
                <p className="text-muted-foreground text-sm">{exercise.category}</p>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary text-xs">
                    {exercise.duration}
                  </Badge>
                  <Badge variant="outline" className={`${getDifficultyColor(exercise.difficulty)} text-xs`}>
                    {exercise.difficulty}
                  </Badge>
                  {exercise.frequency && (
                    <Badge variant="outline" className="text-xs">
                      {exercise.frequency}
                    </Badge>
                  )}
                </div>
              </div>
              {!isActive && (
                <>
                  {isCompleted ? (
                    <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Done
                    </Badge>
                  ) : (
                    <Button
                      className="bg-accent hover:bg-accent/90 flex-shrink-0"
                      onClick={() => handleStartExercise(exercise.id)}
                    >
                      <Play className="w-4 h-4 mr-1" />
                      Start
                    </Button>
                  )}
                </>
              )}
            </div>

            {/* Timer Section for List View */}
            {isActive && (
              <div className="mt-4 space-y-4 pt-4 border-t-2 border-primary/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-muted-foreground">Elapsed</p>
                      <h3 className="text-primary">{formatTime(timerSeconds)}</h3>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-muted-foreground">Remaining</p>
                    <h3 className="text-accent">
                      {formatTime(Math.max(0, prescribedSeconds - timerSeconds))}
                    </h3>
                  </div>
                </div>

                <Progress value={Math.min(timerProgress, 100)} className="h-2" />

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePauseExercise}
                    className="flex-1"
                  >
                    {isTimerRunning ? (
                      <>
                        <Pause className="w-4 h-4 mr-1" />
                        Pause
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-1" />
                        Resume
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetExercise}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-white"
                    onClick={() => handleCompleteExercise(exercise.id)}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {timerSeconds >= prescribedSeconds ? 'Complete' : 'Stop & Save'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancelExercise}
                  >
                    <XCircle className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    // Grid View
    return (
      <Card 
        key={exercise.id} 
        className={`shadow-lg hover:shadow-xl transition-all ${
          isActive ? 'border-2 border-primary' : ''
        }`}
      >
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <CardTitle className="text-primary">{exercise.name}</CardTitle>
                {exercise.isPrescribed && (
                  <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4] text-xs">
                    Prescribed
                  </Badge>
                )}
              </div>
              <p className="text-muted-foreground text-sm">{exercise.category}</p>
              {exercise.frequency && (
                <p className="text-muted-foreground text-xs mt-1">{exercise.frequency}</p>
              )}
            </div>
            <Badge variant="outline" className={getDifficultyColor(exercise.difficulty)}>
              {exercise.difficulty}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-foreground">{exercise.description}</p>

          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary">
              <Clock className="w-3 h-3 mr-1" />
              {exercise.duration}
            </Badge>
            <Badge variant="outline" className="bg-accent/10 text-accent border-accent">
              <Target className="w-3 h-3 mr-1" />
              {exercise.calories}
            </Badge>
            <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]">
              <BookOpen className="w-3 h-3 mr-1" />
              {exercise.steps} Steps
            </Badge>
          </div>

          <div>
            <p className="text-muted-foreground mb-2">Benefits:</p>
            <div className="flex flex-wrap gap-2">
              {exercise.benefits.map((benefit, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-secondary text-foreground rounded-lg"
                >
                  {benefit}
                </span>
              ))}
            </div>
          </div>

          {/* Start/Complete Button */}
          {!isActive && (
            <>
              {isCompleted ? (
                <Badge variant="outline" className="w-full bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4] justify-center py-3">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Completed
                </Badge>
              ) : (
                <Button 
                  className="w-full bg-accent hover:bg-accent/90"
                  onClick={() => handleStartExercise(exercise.id)}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Exercise
                </Button>
              )}
            </>
          )}

          {/* Timer Section for Grid View */}
          {isActive && (
            <div className="space-y-4 pt-4 border-t-2 border-primary/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-muted-foreground">Elapsed</p>
                    <h3 className="text-primary">{formatTime(timerSeconds)}</h3>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-muted-foreground">Remaining</p>
                  <h3 className="text-accent">
                    {formatTime(Math.max(0, prescribedSeconds - timerSeconds))}
                  </h3>
                </div>
              </div>

              <Progress value={Math.min(timerProgress, 100)} className="h-2" />

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePauseExercise}
                  className="flex-1"
                >
                  {isTimerRunning ? (
                    <>
                      <Pause className="w-4 h-4 mr-1" />
                      Pause
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-1" />
                      Resume
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResetExercise}
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button
                  className="bg-[#14CAF4] hover:bg-[#14CAF4]/90 text-white"
                  onClick={() => handleCompleteExercise(exercise.id)}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  {timerSeconds >= prescribedSeconds ? 'Complete' : 'Stop & Save'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancelExercise}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <PatientLayout 
      currentView="library"
      onNavigate={onNavigate}
      onLogout={onLogout}
    >
      {/* Header */}
      <div className="bg-primary p-6 rounded-b-3xl shadow-md">
        <div className="flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="text-primary-foreground">
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}
          <div className="flex-1">
            <h1 className="text-primary-foreground">Exercise Library</h1>
            <p className="text-primary-foreground/80">Your personalized exercise plan</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-primary-foreground/60" />
          <Input
            placeholder="Search exercises..."
            className="pl-12 bg-white/10 border-white/20 text-primary-foreground placeholder:text-primary-foreground/60"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Show warning if custom plans failed to load but don't block the page */}
        {customPlansError && (
          <AlertCard
            type="warning"
            title="Custom plans unavailable"
            description="Your therapist-prescribed plans couldn't be loaded. Showing general exercise library."
          />
        )}
        
        {/* My Exercise Plan Section */}
        {customPlans.length > 0 && (
          <Card className="shadow-lg bg-gradient-to-r from-primary/5 to-accent/5 border-2 border-primary/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-primary flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  My Exercise Plan
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onNavigate?.('exercise-log')}
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                >
                  <BookOpen className="w-4 h-4 mr-1" />
                  View History
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {customPlans.map((plan) => (
                <div key={plan.id} className="mb-3 last:mb-0">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-foreground">{plan.plan_title}</h4>
                    <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]">
                      {plan.status}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground text-sm mb-2">{plan.notes || 'Personalized plan from your therapist'}</p>
                  <div className="flex flex-wrap gap-2">
                    {(plan.exercises || []).map((exercise: any, idx: number) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {exercise.type} • {exercise.duration}min • {exercise.frequency}x/week
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-xl whitespace-nowrap transition-colors ${
                selectedCategory === category.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white text-foreground border border-border'
              }`}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>

        {/* Exercise Stats */}
        <Card className="shadow-lg">
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
                  <Target className="w-6 h-6 text-primary" />
                </div>
                <p className="text-primary text-xl">{allExercises.length}</p>
                <p className="text-muted-foreground text-sm">Available</p>
              </div>
              <div>
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-2">
                  <CheckCircle className="w-6 h-6 text-accent" />
                </div>
                <p className="text-accent text-xl">{completedCount}</p>
                <p className="text-muted-foreground text-sm">Completed</p>
              </div>
              <div>
                <div className="w-12 h-12 rounded-full bg-[#14CAF4]/10 flex items-center justify-center mx-auto mb-2">
                  <Clock className="w-6 h-6 text-[#14CAF4]" />
                </div>
                <p className="text-[#14CAF4] text-xl">{activeExercise ? formatTime(timerSeconds) : '0:00'}</p>
                <p className="text-muted-foreground text-sm">Current</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Exercise List */}
        <Tabs defaultValue="grid" className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="grid">Grid View</TabsTrigger>
            <TabsTrigger value="list">List View</TabsTrigger>
          </TabsList>

          <TabsContent value="grid" className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredExercises.map((exercise) => renderExerciseCard(exercise, false))}
            </div>
          </TabsContent>

          <TabsContent value="list" className="space-y-3">
            {filteredExercises.map((exercise) => renderExerciseCard(exercise, true))}
          </TabsContent>
        </Tabs>

        {/* No Results */}
        {filteredExercises.length === 0 && (
          <Card className="shadow-lg">
            <CardContent className="p-12 text-center">
              <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-primary mb-2">No exercises found</h3>
              <p className="text-muted-foreground">
                Try adjusting your search or filters
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </PatientLayout>
  );
}